import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getFirstUsableImageUrlForCard,
  handleFirstImage,
  handleNavigate,
  isNavigable,
  pickFirstNonEmptyImageField,
  redirectToUrl,
  updateIsEmptyStatus,
} from "../../../../../Utils/StayUpToDate/RenderComponent";
import "../../../../../components/StayUpToDateComponent/StayUpToDateRenderComponent/AdditionalCarousel.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { debounce } from "lodash";
import CustomPagination from "../../../../../components/StayUpToDateComponent/CustomPagination";
import FrontendTemplate from "../../../../../components/StayUpToDateComponent/UpToDateComponents/StructureTemplates/FrontendTemplate";
import { useLoadCollectionComponents } from "./useLoadCollectionComponents";
import {
  getCreatorPageComponentPath,
  getPublicUrlWithUsername,
} from "../../../../../Utils/CustomDomain/CustomDomainFuntions";
import track, { TRACKING_TYPES } from "../../../../../Utils/SupabaseServices/Track";
import trackTemplate, {
  TEMPLATE_TRACKING_TYPES,
} from "../../../../../Utils/SupabaseServices/TrackTemplate";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../../../Utils/SupabaseServices/templateAnalyticsEvents";
import {
  legacyLibAnalyticsPathname,
  shouldMirrorLegacyLibAnalytics,
} from "../../../../../Utils/analytics/legacyLibAnalyticsPath";
import { isPublicWebsiteTemplateRoute } from "../../../../../Utils/websiteTemplateGuards";
import { resolveTemplatePageKeyFromPathname } from "../../../MasterTemplateEditor/websitePublicTemplatePath";
import {
  carouselAnalyticsRank,
  carouselTrackIdentity,
} from "../../../../../Utils/analytics/carouselAnalyticsKey";
import { useCreatorBrowsableUrlPrefs } from "../../../../../context/CreatorBrowsableUrlPrefsContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  getComponentViewTransitionName,
  navigateWithViewTransitionAndSmoothScroll,
} from "../../../../../Utils/viewTransitions";
import { requestEmbedIframeHeightRecalcFor } from "../../../../../Utils/HelperFunction/Useiframeheight";
import { fetchFirstTemplateTabFeedItem } from "../../../MasterTemplateEditor/fetchFirstTemplateTabFeedItem";
import {
  platformKeyFromComponentRow,
  textPlatformNoMediaLabel,
} from "../../../MasterTemplateEditor/contentLooks";
import { NoMediaPost } from "../../../LandingPageCarousels/CommonComponents/NoMediaPost";

dayjs.extend(utc);
dayjs.extend(timezone);

const CollectionComponentFrontend = (props: any) => {
  const { creatorPageSlug } = props;
  const itemsPerPage = 3;
  const {
    user_id,
    username,
    title,
    postIds,
    handleRedirect,
    brand_color,
    isLinks,
    component,
    type,
    RenderUI,
    from,
    
    templateGridLoaderProps,designTokens,
  } = props;

  const param = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const browsableUrlPrefs = useCreatorBrowsableUrlPrefs();
  const customDomain = browsableUrlPrefs?.domain ?? null;
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<any>(null);
  const isButtonClicked = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  // Temporarily disable analytics-driven ranking on public landing/detail views
  // so we can verify whether the dashboard analytics hook is causing crashes.
  const carouselClicksAnalytics = undefined;
  const loadingCarouselClicksAnalytics = false;

  // Load collection components for the given postIds (which are component ids)
  // Handle postIds as either string or array
  const normalizedPostIds = useMemo(() => {
    if (!postIds || postIds === "") return [];
    if (Array.isArray(postIds)) return postIds;
    if (typeof postIds === "string") return postIds.split(",").filter(Boolean);
    return [];
  }, [postIds]);

  const {
    isLoading: CollectionComponentDataLoading,
    data: CollectionComponentData,
  }: any = useLoadCollectionComponents(
    username,
    user_id,
    normalizedPostIds,
    title,
    component?.content_order,
    component?.custom_order?.split(","),
    undefined,
    from
  );
  useEffect(() => {
    const checkEmptyStatus = async () => {
      if (!CollectionComponentDataLoading) {
        if (
          !CollectionComponentData ||
          !CollectionComponentData?.data ||
          CollectionComponentData?.data?.length === 0
        ) {
          await updateIsEmptyStatus(title, user_id, component.id,from);
          setIsLoading(false);
        } else {
          await handleFirstImage(
            title,
            component?.id,
            CollectionComponentData?.data?.[0],
            user_id,
            from
          );
          setIsLoading(false);
        }
      }
    };
    checkEmptyStatus();
  }, [CollectionComponentDataLoading, CollectionComponentData]);

  const handlePrevPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleNextPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) =>
      Math.min(prevIndex + 1, (CollectionComponentData?.data?.length || 0) - itemsPerPage)
    );
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleScroll = useCallback(
    debounce(() => {
      if (!isButtonClicked.current) {
        if (containerRef.current) {
          const scrollLeft = containerRef.current.scrollLeft;
          const itemWidth =
            containerRef.current.scrollWidth /
            (CollectionComponentData?.data?.length || 1);
          const newCurrentIndex = Math.round(scrollLeft / itemWidth);
          setCurrentIndex(newCurrentIndex);
        }
      }
    }, 100),
    [CollectionComponentData]
  );

  useEffect(() => {
    if (containerRef.current) {
      const itemWidth =
        containerRef.current.scrollWidth /
        (CollectionComponentData?.data?.length || 1);
      const scrollPosition = itemWidth * currentIndex;
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [currentIndex, CollectionComponentData]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.addEventListener("scroll", handleScroll);
      return () => {
        if (containerRef.current) {
          containerRef.current.removeEventListener("scroll", handleScroll);
        }
      };
    }
  }, [CollectionComponentData, handleScroll]);

  // Build a ranking map from analytics: component name -> rank (0 = most popular)
  const rankByName = useMemo(() => {
    if (!carouselClicksAnalytics || loadingCarouselClicksAnalytics) {
      return new Map<string, number>();
    }

    // Compute total sums for each component name, then sort descending and map to rank
    const totals = Object.keys(carouselClicksAnalytics).map((key) => {
      const items = (carouselClicksAnalytics as any)[key];
      const totalSum = Array.isArray(items)
        ? items.reduce(
            (sum: number, oneKey: any) =>
              sum +
              (oneKey?.count || 0) +
              (oneKey.id && oneKey.idCount ? oneKey.idCount : 0),
            0
          )
        : 0;
      return { key, totalSum };
    });

    totals.sort((a, b) => b.totalSum - a.totalSum);

    const map = new Map<string, number>();
    totals.forEach((t, idx) => map.set(t.key, idx));
    return map;
  }, [carouselClicksAnalytics, loadingCarouselClicksAnalytics]);

  // Normalize collection records so they look like regular feed posts for FrontendTemplate/ImagePostTag
  const normalizedData = useMemo(() => {
    if (!CollectionComponentData?.data || !Array.isArray(CollectionComponentData.data)) {
      return [];
    }
    let data = CollectionComponentData.data.map((item: any) => {
      const cardImage =
        item?.card_image || item?.image || item?.media_url || "";
      return {
        ...item,
        media_url: item?.media_url || cardImage,
        product_image: item?.product_image || cardImage,
        product_name: item?.name,
      };
    });

    // Apply POPULAR sorting only for carousel-collection components
    if (component?.type === "carousel-collection" && component?.content_order === "POPULAR" && rankByName.size > 0) {
      data = [...data].sort((a, b) => {
        const rankA = carouselAnalyticsRank(rankByName, a);
        const rankB = carouselAnalyticsRank(rankByName, b);
        
        return rankA - rankB; // Lower rank = more popular
      });
    }

    return data;
  }, [CollectionComponentData?.data, component?.type, component?.content_order, rankByName]);

  const [previewByComponentId, setPreviewByComponentId] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    let cancelled = false;
    const resolvePreviewForComponent = async (childComponent: any) => {
      const id = String(childComponent?.id ?? childComponent?.component_id ?? "");
      if (!id) return;
      const existing =
        pickFirstNonEmptyImageField(childComponent?.card_image) ||
        previewByComponentId[id];
      if (existing) return;
      try {
        const firstItem = await fetchFirstTemplateTabFeedItem(
          user_id,
          username,
          childComponent,
        );
        if (!firstItem) return;
        const imageToUse = getFirstUsableImageUrlForCard(firstItem);
        if (!imageToUse) return;
        if (!cancelled) {
          setPreviewByComponentId((prev) => ({ ...prev, [id]: imageToUse }));
        }
        if (childComponent?.is_card_image_updated !== true) {
          await handleFirstImage(
            childComponent?.name,
            childComponent?.id,
            firstItem,
            user_id,
            from,
          );
        }
      } catch {
        // best-effort
      }
    };
    const run = async () => {
      const items = Array.isArray(normalizedData) ? normalizedData : [];
      const batchSize = 3;
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        await Promise.all(batch.map(resolvePreviewForComponent));
        if (cancelled) break;
      }
    };
    if (username && user_id && normalizedData?.length) {
      void run();
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedData, username, user_id, from]);

  // Clamp currentIndex when data size shrinks
  useEffect(() => {
    const totalItems = normalizedData?.length || 0;
    const maxStart = Math.max(0, totalItems - itemsPerPage);
    if (currentIndex > maxStart) {
      setCurrentIndex(Math.max(0, maxStart));
    }
  }, [normalizedData?.length, itemsPerPage, currentIndex]);

  const handleNavigateElement = useCallback(
    (component: any) => {
      if (!isNavigable(component)) return;
      const componentSlug = component.route_name || component.unique_id;
      const creatorPageComponentPath = getCreatorPageComponentPath(
        componentSlug,
        location,
        creatorPageSlug ?? component?.creatorPageSlug,
        username,
      );
      const destination =
        creatorPageComponentPath ||
        getPublicUrlWithUsername(username, componentSlug);

      navigateWithViewTransitionAndSmoothScroll(() => {
        navigate(destination, { state: { data: component } });
      }, window);

      requestEmbedIframeHeightRecalcFor(from);

      const identity = carouselTrackIdentity(component);
      const path = location.pathname || "";
      const pageKey =
        resolveTemplatePageKeyFromPathname(path) || "linkInBio";
      const libPath = legacyLibAnalyticsPathname(username);

      if (isPublicWebsiteTemplateRoute(path)) {
        void trackTemplate(
          TEMPLATE_ANALYTICS_EVENTS.CAROUSEL_TAB,
          TEMPLATE_TRACKING_TYPES.EVENT,
          {
            pathname: path,
            details: identity,
            user_id,
            username,
            page_key: pageKey,
            entity_type: "carousel_tab",
            entity_label: component?.name,
            entity_id: identity.component_id || identity.unique_id,
            carousel_slug: identity.component_id || identity.unique_id,
          },
        );
        if (shouldMirrorLegacyLibAnalytics(path, pageKey)) {
          track("Carousel Click", TRACKING_TYPES.EVENT, {
            pathname: libPath,
            details: identity,
            user_id,
            title: "Stay Up To Date",
          });
        }
      } else {
        track("Carousel Click", TRACKING_TYPES.EVENT, {
          pathname: libPath,
          details: identity,
          user_id,
          title: "Stay Up To Date",
        });
        // Classic LiB page-view bump on collection navigate (skip on public website).
        if ("requestIdleCallback" in window) {
          (window as any).requestIdleCallback(() =>
            track(`/${username}`, TRACKING_TYPES.PAGE, {
              pathname: `/${username}`,
              details: identity,
              user_id,
              title: "Stay Up To Date",
            }),
          );
        } else {
          Promise.resolve().then(() =>
            track(`/${username}`, TRACKING_TYPES.PAGE, {
              pathname: `/${username}`,
              details: identity,
              user_id,
              title: "Stay Up To Date",
            }),
          );
        }
      }
    },
    [creatorPageSlug, from, location.pathname, navigate, user_id, username]
  );

  const showTemplateTextLoading = from === "website-template";

  return (
    <>
      {CollectionComponentDataLoading || isLoading ? (
        showTemplateTextLoading ? null : (
          <div className="mainDiv">
            <div
              className="wrapper-div"
              style={{
                display: "flex",
                flexWrap: "nowrap",
                gap: "9px",
                overflowX: "auto",
              }}
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="product-card"
                  aria-hidden
                  style={{
                    flex: "0 0 auto",
                    minWidth: "215px",
                    width: "215px",
                    height: "215px",
                    boxSizing: "border-box",
                    background: "transparent",
                    backgroundColor: "transparent",
                    border: `1px solid ${
                      designTokens?.tertiaryColor || "#e0e0e0"
                    }`,
                    boxShadow: "none",
                  }}
                />
              ))}
            </div>
          </div>
        )
      ) : (CollectionComponentData && !CollectionComponentData?.data) ||
        CollectionComponentData?.data?.length == 0 ? null : (
        <>
          {RenderUI ? (
            <RenderUI
              data={normalizedData}
              currentIndex={currentIndex}
              handleNavigate={handleNavigate}
              component={component}
              handleNextPage={handleNextPage}
              handlePrevPage={handlePrevPage}
              containerRef={containerRef}
              handleRedirect={handleRedirect}
              brand_color={brand_color}
              title={title}
              type={type}
              isLinks={isLinks}
              from={from}
              username={username}
              user_id={user_id}
              templateGridLoaderProps={templateGridLoaderProps}
            />
          ) : normalizedData && normalizedData?.length > 0 ? (
            <div>
              <div className="mainDiv">
                <p
                  className="component-name"
                  style={{
                    ...(param?.id ? { cursor: "auto" } : {}),
                    viewTransitionName: getComponentViewTransitionName(component),
                    fontFamily: designTokens?.primaryFontFamily,
                  }}
                  onClick={(e) =>
                    handleNavigate(
                      navigate,
                      location,
                      username,
                      user_id,
                      component,
                      from,
                      undefined,
                      creatorPageSlug ?? component?.creatorPageSlug,
                      customDomain,
                      browsableUrlPrefs,
                    )
                  }
                >
                  {title}
                </p>
                {/* {!param?.id && (
                  <CustomPagination
                    totalItems={CollectionComponentData?.total_count}
                    currentIndex={currentIndex}
                    handlePrevPage={handlePrevPage}
                    handleNextPage={handleNextPage}
                  />
                )} */}
              </div>

              <div
                className="wrapper-div"
                ref={containerRef}
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  gap: "9px",
                  overflowX: "auto",
                  cursor: "pointer",
                  // padding: "8px 4px",
                }}
              >
                {normalizedData.map((elem: any, idx: number) => {
                  const previewResolved =
                    previewByComponentId[
                      String(elem?.id ?? elem?.component_id ?? "")
                    ];
                  const imageSrc =
                    pickFirstNonEmptyImageField(elem?.card_image) ||
                    pickFirstNonEmptyImageField(previewResolved) ||
                    pickFirstNonEmptyImageField(elem?.image) ||
                    pickFirstNonEmptyImageField(elem?.media_url) ||
                    "";
                  const displayName =
                    elem?.name || elem?.product_name || elem?.title || "";
                  const key = elem?.id || elem?.post_id || `${idx}-${elem?.id || elem?.post_id || "item"}`;
                  const platformKey = platformKeyFromComponentRow(elem);

                  return (
                    <div
                      key={key}
                      className="product-card"
                      onClick={() => handleNavigateElement(elem)}
                      style={{
                        flex: "0 0 auto",
                        minWidth: "215px",
                        viewTransitionName: getComponentViewTransitionName(elem),
                        border: designTokens?.secondaryColor ? `none` : "",
                      }}
                    >
                      {imageSrc ? (
                        <div
                          className="product-image"
                          style={{
                            backgroundImage: `url("${imageSrc}")`,
                          }}
                        >
                          <img src={imageSrc} alt="" />
                          <p
                            className="product-text"
                            style={{
                              color: "#ffffff",
                              fontFamily: designTokens?.secondaryFontFamily,
                            }}
                          >
                            {displayName}
                          </p>
                        </div>
                      ) : (
                        // Same 215×215 shell as .product-image so LinkedIn/X
                        // text tiles match image siblings (not feed h-250).
                        <div className="product-image product-image--nomedia">
                          <NoMediaPost
                            post={{
                              platform: platformKey,
                              caption: displayName,
                              post_date: elem?.post_date || elem?.created_at,
                            }}
                            type={platformKey || "content"}
                            label={textPlatformNoMediaLabel(platformKey)}
                            className="newsletter-media-carousel no-media-url square-image"
                            showDate={false}
                            showMeta={false}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            ""
          )}
        </>
      )}
    </>
  );
};

export default CollectionComponentFrontend;
